/**
 * The discography page's actions menu: every toolbar CTA (Create recording,
 * Bulk Edit, Validate, Expand/Collapse All, Reset Sort, Reset Filters)
 * behind one burger button. The column selector (Show/Hide Data) stays
 * beside it, not inside — it is a view setting, not an action.
 *
 * The trigger is a hand-rolled `<button>` rather than a `WButton`: `WButton`
 * renders a `<div onClick>` with no `role`, `tabIndex`, keyboard handling or
 * `aria-label` prop, so it cannot carry an icon-only control accessibly. The
 * classes reproduce `WButton`'s `primary` variant so the two read as the same
 * control family, and the divider-separated group keeps the menu physically
 * apart from the view/filter utilities on its left.
 */
import React from 'react';
import {
  ArrowUpDown,
  ChevronsUpDown,
  FilterX,
  ListChecks,
  Menu,
  Pencil,
  Plus,
} from 'lucide-react';
import WDropdown from 'shared_components/components/WDropdownContent/WDropdown';

export type DiscographyActionsMenuItem = {
  label: string;
  icon: 'create' | 'edit' | 'expand' | 'sort' | 'filter' | 'select';
  onClick: () => void;
};

const MENU_ICONS: Record<DiscographyActionsMenuItem['icon'], React.ReactNode> =
  {
    create: <Plus className="tw-w-4 tw-h-4" />,
    edit: <Pencil className="tw-w-4 tw-h-4" />,
    expand: <ChevronsUpDown className="tw-w-4 tw-h-4" />,
    sort: <ArrowUpDown className="tw-w-4 tw-h-4" />,
    filter: <FilterX className="tw-w-4 tw-h-4" />,
    select: <ListChecks className="tw-w-4 tw-h-4" />,
  };

export const DiscographyActionsMenu: React.FC<{
  items: DiscographyActionsMenuItem[];
}> = ({ items }) => (
  <div className="tw-flex tw-items-center tw-border-l tw-border-formInputBackground tw-pl-3 tw-ml-1">
    <WDropdown
      closeOnContentClick
      trigger={
        <button
          type="button"
          aria-label="Discography actions"
          title="Actions"
          className="tw-flex tw-items-center tw-justify-center tw-h-[32px] tw-w-[32px] tw-rounded-3xl tw-bg-surfaceLime tw-text-surfaceBlack90 tw-cursor-pointer focus:tw-outline-none focus-visible:tw-ring-2 focus-visible:tw-ring-white"
        >
          <Menu className="tw-w-4 tw-h-4" strokeWidth={2.5} />
        </button>
      }
      content={
        <div className="tw-min-w-[220px] tw-py-1">
          {items.map((item) => (
            <div
              key={item.label}
              role="button"
              tabIndex={0}
              className="tw-px-3 tw-py-2 tw-flex tw-items-center tw-gap-3 tw-text-sm tw-whitespace-nowrap tw-text-white tw-cursor-pointer hover:tw-bg-neutral-800"
              onClick={item.onClick}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  item.onClick();
                }
              }}
            >
              <span className="tw-text-neutral-400">
                {MENU_ICONS[item.icon]}
              </span>
              {item.label}
            </div>
          ))}
        </div>
      }
    />
  </div>
);
